import React, { createContext, useState, useMemo, useContext, useEffect } from "react";
import {
    createTheme,
    ThemeProvider as MuiThemeProvider,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

const ThemeContext = createContext();

export const useThemeMode = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useThemeMode must be used within ThemeProvider");
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    const [mode, setMode] = useState(
        () => localStorage.getItem("themeMode") || "light",
    );

    useEffect(() => {
        document.documentElement.setAttribute("data-theme", mode);
    }, [mode]);

    const toggleTheme = () => {
        setMode((prevMode) => {
            const next = prevMode === "light" ? "dark" : "light";
            localStorage.setItem("themeMode", next);
            return next;
        });
    };

    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                    primary: {
                        main: "#4B76A5",
                        light: "#6D94C0",
                        dark: "#35527A",
                        contrastText: "#fff",
                    },
                    secondary: {
                        main: "#EF6924",
                        light: "#F4874E",
                        dark: "#D8541A",
                        contrastText: "#fff",
                    },
                    background: {
                        default: mode === "light" ? "#ffffff" : "#070f1a",
                        paper: mode === "light" ? "#ffffff" : "#0d1929",
                    },
                    text: {
                        primary: mode === "light" ? "#1a1a1a" : "#e8edf4",
                        secondary: mode === "light" ? "#424242" : "#8fabc8",
                    },
                    divider:
                        mode === "light"
                            ? "rgba(0, 0, 0, 0.18)"
                            : "rgba(100, 160, 220, 0.2)",
                },
                typography: {
                    fontFamily: [
                        "Lato",
                        "Roboto",
                        "-apple-system",
                        "BlinkMacSystemFont",
                        '"Segoe UI"',
                        '"Helvetica Neue"',
                        "Arial",
                        "sans-serif",
                    ].join(","),
                    h1: { fontSize: "2.5rem", fontWeight: 700, letterSpacing: "-0.01562em" },
                    h2: { fontSize: "2rem", fontWeight: 700, letterSpacing: "-0.00833em" },
                    h3: { fontSize: "1.75rem", fontWeight: 700, letterSpacing: "0em" },
                    h4: { fontSize: "1.5rem", fontWeight: 700, letterSpacing: "0.00735em" },
                    h5: { fontSize: "1.25rem", fontWeight: 700, letterSpacing: "0em" },
                    h6: { fontSize: "1rem", fontWeight: 700, letterSpacing: "0.0075em" },
                    body1: { fontSize: "1rem", fontWeight: 400, lineHeight: 1.5 },
                    body2: { fontSize: "0.875rem", fontWeight: 400, lineHeight: 1.43 },
                    button: { fontWeight: 700, letterSpacing: "0.02857em", textTransform: "uppercase" },
                },
                shape: {
                    borderRadius: 4,
                },
                components: {
                    MuiButton: {
                        styleOverrides: {
                            root: {
                                textTransform: "uppercase",
                                fontWeight: 700,
                                borderRadius: 4,
                                padding: "10px 24px",
                                fontSize: "0.875rem",
                            },
                            contained: {
                                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.15)",
                                "&:hover": { boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)" },
                                "&:active": { boxShadow: "0 1px 2px rgba(0, 0, 0, 0.15)" },
                            },
                            containedPrimary: {
                                background: "#4B78A6",
                                color: "#ffffff",
                                "&:hover": { background: "#355574" },
                            },
                            containedSecondary: {
                                background: "#F16826",
                                color: "#ffffff",
                                "&:hover": { background: "#D8541A" },
                            },
                            outlined: {
                                background: mode === "light" ? "#ffffff" : "#0d1929",
                                color: "#4B78A6",
                                borderWidth: "1px",
                                borderColor: "#4B78A6",
                                "&:hover": {
                                    background: mode === "light" ? "#f5f5f5" : "#13243d",
                                    borderWidth: "1px",
                                    borderColor: "#355574",
                                },
                            },
                        },
                    },
                    MuiCard: {
                        defaultProps: { elevation: 0 },
                        styleOverrides: {
                            root: {
                                boxShadow:
                                    mode === "light"
                                        ? "0 1px 3px rgba(0, 0, 0, 0.08)"
                                        : "0 1px 3px rgba(0, 0, 0, 0.4)",
                                border:
                                    mode === "light"
                                        ? "1px solid rgba(0, 0, 0, 0.15)"
                                        : "1px solid rgba(100, 160, 220, 0.2)",
                            },
                        },
                    },
                    MuiAppBar: {
                        styleOverrides: {
                            root: { boxShadow: "none" },
                        },
                    },
                    MuiPaper: {
                        defaultProps: { elevation: 0 },
                        styleOverrides: {
                            root: { backgroundImage: "none" },
                            outlined: {
                                borderWidth: "1px",
                                borderColor:
                                    mode === "light"
                                        ? "rgba(0, 0, 0, 0.15)"
                                        : "rgba(100, 160, 220, 0.2)",
                            },
                        },
                    },
                    MuiTextField: {
                        styleOverrides: {
                            root: {
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderWidth: "1px",
                                        borderColor:
                                            mode === "light"
                                                ? "rgba(0, 0, 0, 0.23)"
                                                : "rgba(100, 160, 220, 0.3)",
                                    },
                                    "&:hover fieldset": {
                                        borderColor:
                                            mode === "light"
                                                ? "rgba(0, 0, 0, 0.4)"
                                                : "rgba(100, 160, 220, 0.6)",
                                    },
                                },
                            },
                        },
                    },
                    MuiDivider: {
                        styleOverrides: {
                            root: {
                                borderColor:
                                    mode === "light"
                                        ? "rgba(0, 0, 0, 0.15)"
                                        : "rgba(100, 160, 220, 0.2)",
                            },
                        },
                    },
                    MuiTableCell: {
                        styleOverrides: {
                            root: { fontWeight: 400 },
                            head: { fontWeight: 700 },
                        },
                    },
                    MuiIconButton: {
                        styleOverrides: {
                            root: {
                                color: mode === "dark" ? "#e8edf4" : undefined,
                                "& svg": { color: mode === "dark" ? "#e8edf4" : undefined },
                            },
                        },
                    },
                    MuiInputAdornment: {
                        styleOverrides: {
                            root: {
                                color: mode === "dark" ? "#e8edf4" : undefined,
                                "& svg": { fill: mode === "dark" ? "#e8edf4" : undefined },
                            },
                        },
                    },
                },
            }),
        [mode],
    );

    return (
        <ThemeContext.Provider value={{ mode, toggleTheme }}>
            <MuiThemeProvider theme={theme}>
                <CssBaseline />
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};
